import { useAppSelector } from '../app/hooks';

// Mirrors the level numbering the API uses (admin=3, manager=2,
// customer=1). Reading the legacy `state.authentication.user` directly
// instead of bridging through a v2 slice — auth is the global session
// bootstrap, not a feature, and the legacy thunk is what hydrates it.
export const ROLE_LEVELS = {
    CUSTOMER: 1,
    MANAGER: 2,
    ADMIN: 3,
} as const;

type AuthUser = {
    id?: number;
    email?: string;
    name?: string;
    role?: { level?: number; name?: string };
} | null;

// RootState is `Record<string, unknown> & { v2Api: ... }` until
// configureStore.js migrates to TS, so `state.authentication` lands
// here as `unknown`. Narrow at read-time rather than typing the
// selector's parameter — a narrowed parameter type is rejected by
// `useAppSelector` for not overlapping with RootState.
export function useCurrentUser(): AuthUser {
    return useAppSelector((state) => {
        const auth = state.authentication as { user?: AuthUser } | undefined;
        return auth?.user ?? null;
    });
}

export function useIsAdmin(): boolean {
    const user = useCurrentUser();
    return !!user && (user.role?.level ?? 0) >= ROLE_LEVELS.ADMIN;
}

// Manager or Admin, but NOT Staff (which is also level 2). Mirrors the API's
// Auth.isManagerOrAdmin — used to gate manager-only affordances (e.g. recolouring
// labels) that Staff shouldn't get even though they share the level.
export function useIsManagerOrAdmin(): boolean {
    const user = useCurrentUser();
    return (
        !!user &&
        (user.role?.level ?? 0) >= ROLE_LEVELS.MANAGER &&
        user.role?.name !== 'Staff'
    );
}

// Who may open Raport general: admins (all data) and Managers (their own store
// only). Staff and Shop kiosk accounts — level 2 but not 'Manager' — get
// nothing, mirroring the API's report scoping. Returns `undefined` while the
// profile is still loading (same two-step hydration caveat as useAuthLevel).
export function useCanViewReports(): boolean | undefined {
    const user = useCurrentUser();
    const level = user?.role?.level;
    if (level === undefined) return undefined;
    return level >= ROLE_LEVELS.ADMIN || user?.role?.name === 'Manager';
}

// The user's role level, or `undefined` while the profile is still loading.
// On a hard refresh checkAuth() runs in two steps: first loginThunks.success is
// dispatched with ONLY a token (no user) — and the reducer's initial `user` is
// `{}` (truthy!) — so the role isn't known yet for a beat. We must key off the
// *level* itself, not the presence of `user`: `{}?.role?.level` and
// `tokenOnly.role?.level` both yield `undefined` (= still loading), while a fully
// hydrated profile yields the real number. Do NOT default a missing level to
// CUSTOMER — that conflates "loading" with "level 1" and makes manager-gated
// screens bounce to /v2 (→ products) on every refresh. Pair with a loading state.
export function useAuthLevel(): number | undefined {
    return useAppSelector((state) => {
        const auth = state.authentication as { user?: AuthUser } | undefined;
        return auth?.user?.role?.level;
    });
}
